'use client';

import type { InfiniteScrollParams } from '@lib/data/products';
import { useInfiniteProducts } from '@lib/hooks/use-infinite-products';
import { useScrollObserver } from '@lib/hooks/use-scroll-observer';
import ProductPreview from '@modules/products/components/product-preview';

type InfiniteProductGridProps = {
  // Pre-rendered initial product cards from the server
  children: React.ReactNode;
  totalCount: number;
} & (
  | {
      /** All products pre-loaded (price sort/filter) — no further fetching */
      mode: 'client';
    }
  | {
      /** Fetch subsequent pages from server on scroll */
      mode: 'server';
      fetchParams: Omit<InfiniteScrollParams, 'offset' | 'limit'>;
      initialCount: number;
    }
);

export default function InfiniteProductGrid(props: InfiniteProductGridProps) {
  const { children, totalCount, mode } = props;
  const fetchParams = mode === 'server' ? props.fetchParams : undefined;
  const initialCount = mode === 'server' ? props.initialCount : 0;

  const { extraProducts, hasMore, isPending, loadMoreRef } =
    useInfiniteProducts({ mode, totalCount, initialCount, fetchParams });
  const sentinelRef = useScrollObserver(hasMore, loadMoreRef);

  return (
    <>
      <ul
        className="grid grid-cols-2 desktop:grid-cols-4 gap-x-4 gap-y-6 mobile:gap-x-6 mobile:gap-y-8 desktop:gap-x-10 desktop:gap-y-10"
        data-testid="products-list"
      >
        {children}
        {extraProducts.map((p) => (
          <li key={p.id}>
            <ProductPreview card={p} />
          </li>
        ))}
      </ul>
      <GridFooter
        hasMore={hasMore}
        isPending={isPending}
        sentinelRef={sentinelRef}
      />
    </>
  );
}

type GridFooterProps = {
  hasMore: boolean;
  isPending: boolean;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
};

function GridFooter({ hasMore, isPending, sentinelRef }: GridFooterProps) {
  if (hasMore) {
    return (
      <div ref={sentinelRef} className="flex justify-center py-8">
        <div className="text-sm text-fg-neutral-soft">
          {isPending ? 'Loading more...' : ' '}
        </div>
      </div>
    );
  }

  return null;
}
